import { faFileInvoice } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { DataType } from '@globaleris/ui.library'
import { CardContent, Grid, makeStyles, useTheme } from '@material-ui/core'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { EnumEstadoFactura, EnumTipoSujeto } from '../../../../../api'
import { CardWithTransition } from '../../../../../components/shared/cardWithTransition/CardWithTransition'
import { enumEnumToValueNumber, enumToSelectOptionArray } from '../../../../../helpers'
import { DateFilters, DatePeriod } from '../../../../../model'

import { IconProp } from '@fortawesome/fontawesome-svg-core'
import moment from 'moment'
import { useSelector } from 'react-redux'
import CustomFilterV2 from '../../../../../components/shared/customFilter/customFilterV2'
import DateFilter from '../../../../../components/shared/dateFilter/dateFilter'
import { parseDateFilter } from '../../../../../components/shared/dateFilter/dateFilter.helpers'
import { usePageState } from '../../../../../hooks'
import { storeDateFilterForCurrentPage } from '../../../../../redux'
import { selectClientData } from '../../../../../redux/selectors/client.selectors'
import { InvoicesTable } from '../../../../invoicesPage/components/invoicesTable/invoicesTable'
import { FilterDateBy } from '../../../../invoicesPage/invoicesPage.Model'
import { fetchInvoices } from '../../../../invoicesPage/invoicesPage.service'
import { Header } from '../shared/header'
import { styles } from './invoicesCard.styles'

type InvoicesCardProps = {
  defaultDateFilter?: DateFilters
}

export const InvoicesCard: React.FC<InvoicesCardProps> = ({
  defaultDateFilter = DateFilters.THREE_MONTHS
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'clientFile', 'invoicesPage', 'enums'])
  const history = useHistory()

  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()

  const clientData = useSelector(selectClientData)

  const { dataTableFilter, dataTableSearch, dateFilter } = usePageState()

  const [activeDateFilter, setActiveDateFilter] = useState<DateFilters | undefined>(
    dateFilter?.activeDateFilter || defaultDateFilter
  )

  const [dateRange, setDateRange] = React.useState<DatePeriod>({
    start: dateFilter
      ? moment(dateFilter.initDate).toDate()
      : parseDateFilter(defaultDateFilter, 'start'),
    end: dateFilter
      ? moment(dateFilter.endDate).toDate()
      : parseDateFilter(defaultDateFilter, 'end'),
    customFilter: {
      dateFilterType: FilterDateBy.EMISSION,
      subjectCode: clientData?.description,
      subjectId: clientData?.id,
      invoiceStates: [EnumEstadoFactura.Emitida]
    }
  })

  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [filteredData, setFilteredData] = useState<any[] | undefined>(undefined)
  const [appliedCustomFilter, setAppliedCustomFilter] = useState<any>(undefined)

  const pendingFilterRef = React.useRef<any>(null)

  const fetchInvoicesData = async (filterToApply: any): Promise<any[]> => {
    pendingFilterRef.current = filterToApply
    const clientType = enumEnumToValueNumber(EnumTipoSujeto, clientData?.type)!
    const response = await fetchInvoices({
      clientType,
      dateRange: { start: filterToApply.startDate, end: filterToApply.endDate },
      invoiceCode: filterToApply.invoiceCode,
      subjectCode: clientData?.description ?? '',
      subjectId: clientData?.id,
      contractCode: filterToApply.contractCode,
      cupsCode: filterToApply.cupsCode,
      filterDateBy: filterToApply.dateFilterType ?? FilterDateBy.EMISSION,
      invoiceStates: [EnumEstadoFactura.Emitida]
    })
    if (response.success && response.data) {
      return response.data
    }
    return []
  }

  const handleShowResults = (results: any[]) => {
    const filterToApply = pendingFilterRef.current
    if (!filterToApply) return
    setFilteredData(results)
    setAppliedCustomFilter(filterToApply)
    setDateRange({
      start: filterToApply.startDate,
      end: filterToApply.endDate,
      customFilter: {
        dateFilterType: filterToApply.dateFilterType,
        contractCode: filterToApply.contractCode,
        cupsCode: filterToApply.cupsCode,
        invoiceCode: filterToApply.invoiceCode
      }
    })
    setActiveDateFilter(undefined)
  }

  const searchFilterInputs = [
    {
      name: 'dateFilterType',
      label: t('invoicesPage:filters.inputs.dateFilterType'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(FilterDateBy, 'enums:FilterDateBy')
    },
    {
      name: 'invoiceCode',
      label: t('invoicesPage:filters.inputs.invoiceCode'),
      type: DataType.TEXT,
      helpText: t('invoicesPage:filters.helpText.minChar')
    },
    {
      name: 'contractCode',
      label: t('invoicesPage:filters.inputs.contractCode'),
      type: DataType.TEXT,
      helpText: t('invoicesPage:filters.helpText.minChar')
    },
    {
      name: 'cupsCode',
      label: t('invoicesPage:filters.inputs.cupsCode'),
      type: DataType.TEXT,
      helpText: t('invoicesPage:filters.helpText.minChar')
    }
  ]

  const InvoicesHeader = () =>
    React.useMemo(() => {
      return (
        <Header
          avatarIcon={<FontAwesomeIcon size={'lg'} icon={faFileInvoice as IconProp} />}
          title={t('clientFile:components.invoicesCard.title')}
          titleVariant="secondary"
          action={
            <Grid container justify="flex-end" style={{ marginTop: '16px' }}>
              <div style={{ marginRight: '16px' }}>
                <DateFilter
                  inactive={filteredData !== undefined}
                  enabled={!isFetching}
                  initialFilter={
                    dateFilter?.activeDateFilter === null ||
                    dateFilter?.activeDateFilter === undefined
                      ? defaultDateFilter
                      : dateFilter.activeDateFilter
                  }
                  onSelectDate={onSelectDateHandler}
                  filters={[
                    {
                      filter: defaultDateFilter,
                      label:
                        defaultDateFilter === DateFilters.TWELVE_MONTHS
                          ? t('translation:components.dateFilter.lastYear')
                          : t('translation:components.dateFilter.lastThreeMonths')
                    }
                  ]}
                ></DateFilter>
              </div>
              <CustomFilterV2
                onFetchData={fetchInvoicesData}
                onShowResults={handleShowResults}
                additionalFilters={searchFilterInputs}
                appliedFilter={appliedCustomFilter}
                initialState={{ dateFilterType: FilterDateBy.EMISSION }}
                enabled={!isFetching}
                asPopover
              />
            </Grid>
          }
        ></Header>
      )
    }, [t])

  const onSelectDateHandler = React.useCallback(
    (startDate: Date, endDate: Date, activeDateFilter?: DateFilters): void => {
      setFilteredData(undefined)
      setAppliedCustomFilter(undefined)
      setDateRange(prev => ({
        start: startDate,
        end: endDate,
        customFilter: {
          ...prev.customFilter,
          dateFilterType: FilterDateBy.EMISSION
        }
      }))
      setActiveDateFilter(activeDateFilter ? activeDateFilter : defaultDateFilter)
      storeDateFilterForCurrentPage({
        activeDateFilter: activeDateFilter,
        initDate: startDate,
        endDate: endDate
      })
    },
    []
  )

  return (
    <>
      <CardWithTransition show={true} raised>
        <InvoicesHeader />
        <>
          <CardContent className={classes.cardContent}>
            <InvoicesTable
              clientType={enumEnumToValueNumber(EnumTipoSujeto, clientData?.type!)!}
              dateRange={dateRange}
              activeDateFilter={activeDateFilter ?? defaultDateFilter}
              dataTableFilter={dataTableFilter}
              customFilter={{
                ...appliedCustomFilter,
                subjectCode: clientData?.description ?? '',
                subjectId: clientData?.id,
                invoiceStates: [EnumEstadoFactura.Emitida]
              }}
              dataTableSearch={dataTableSearch}
              data={filteredData}
              config={{
                elevation: 0,
                rowsPerPage: 10,
                rowsPerPageOptions: [10, 20],
                queryInvoiceStates: [EnumEstadoFactura.Emitida],
                hideTableColumns: ['subject', 'subjectSource', 'status']
              }}
            ></InvoicesTable>
          </CardContent>
        </>
      </CardWithTransition>
    </>
  )
}
